// ============================================
// PARAMÈTRES APPLICATIFS — ÉCRAN
// ============================================
// Rend le catalogue tel que le backend le décrit : aucune clé n'est codée ici
// (sauf l'aide propre à l'IA). Un paramètre ajouté par une mise à jour du
// serveur apparaît donc seul, sans toucher à cet écran.
//
// Refonte du 13/10/2026, pour un administrateur non technicien :
// - une rubrique à la fois, choisie dans un menu qui porte un voyant par
//   rubrique (En service, À compléter, Désactivé) ; la recherche parcourt
//   toutes les rubriques ;
// - plus d'étiquettes empilées ni de nom technique : ce qui manque est dit
//   une fois en tête de rubrique, et une ligne ne porte d'avertissement que
//   si un interrupteur allumé reste sans effet ;
// - un interrupteur ou une liste s'enregistre dès le clic, et revient en
//   arrière si le serveur refuse ; un champ s'enregistre par son bouton
//   (ou Entrée), actif seulement quand la saisie a changé ;
// - une clé secrète se saisit masquée, peut s'afficher le temps de la
//   vérifier, et la clé d'IA se teste sans quitter l'écran.
//
// 19/10/2026 : entrée « Consommation de l'IA » dans le menu des rubriques
// (jetons, appels, coût estimé ; features/ia/ConsommationIA.tsx). Elle ne
// charge rien tant qu'elle n'est pas ouverte.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, BarChart3, Check, CheckCircle2, ChevronRight, Eye, EyeOff, FolderOpen, Info, Loader2, Mail, Phone, RotateCcw, Search,
  ShoppingBag, SlidersHorizontal, Sparkles, XCircle, Zap,
} from 'lucide-react';
import ConsommationIA from '../ia/ConsommationIA';
import { AdminApi } from '../../services/api';
import type { CategorieParametres, EtatDependances, ParametreDetaille } from '../../services/api';
import { Button, Card, Input, Modal, Select } from '../../shared/ui';
import {
  DEPENDANCES, LIBELLES_VOYANT, dependancesRubrique, doitRelireDependances, filtrerParametres, grouperParCategorie,
  libelleOption, libelleValeurParDefaut, messageErreur, messageTestIA, nombreAEnvoyer, normaliser, texteAttente,
  texteAttenteSecret, texteBornes, texteSansEffet, texteSuccesTestIA, validerNombre, voyantRubrique,
  INTERRUPTEURS_GENERAUX, MODELES_IA, MODELE_AUTRE, choixModele, type GroupeParametres, type Voyant,
} from './logiqueParametres';

// Mobile et tablette : cibles tactiles d'au moins 40 px. À partir de lg
// (ordinateur), les classes s'annulent.
const TACTILE = 'min-h-[40px] lg:min-h-0';
// Champs : Input et Select (shared/ui) sont déjà en w-full et passent en 16 px
// sous sm (max-sm:text-base, pas de zoom iOS). La largeur vient de leur
// enveloppe : une ligne entière sur téléphone (« flex-1 min-w-0
// max-sm:basis-full », les boutons passent dessous), une largeur fixe à
// partir de sm.

type EtatTestIA =
  | { etat: 'en-cours' }
  | { etat: 'ok'; texte: string }
  | { etat: 'echec'; titre: string; detail: string | null };

/** Copie d'un dictionnaire sans une clé. */
function sans<T>(objet: Record<string, T>, cle: string): Record<string, T> {
  if (!(cle in objet)) return objet;
  const copie = { ...objet };
  delete copie[cle];
  return copie;
}

function iconeRubrique(nom: string, className = 'w-4 h-4'): React.ReactNode {
  const n = normaliser(nom);
  if (n.includes('intelligence')) return <Sparkles className={className} aria-hidden />;
  if (n.includes('mail')) return <Mail className={className} aria-hidden />;
  if (n.includes('dossier')) return <FolderOpen className={className} aria-hidden />;
  if (n.includes('prestashop')) return <ShoppingBag className={className} aria-hidden />;
  if (n.includes('telephonie')) return <Phone className={className} aria-hidden />;
  return <SlidersHorizontal className={className} aria-hidden />;
}

const COULEUR_VOYANT: Record<Voyant, { point: string; texte: string }> = {
  ok: { point: 'bg-statut-resolu', texte: 'text-statut-resolu' },
  'a-completer': { point: 'bg-statut-attente', texte: 'text-statut-attente' },
  inactif: { point: 'bg-graphite/40', texte: 'text-graphite' },
};

/** Voyant d'une rubrique : un point de couleur et un mot. `compact` : le mot
 *  n'est lu que par les lecteurs d'écran sous lg (menu en bandeau). */
const Pastille: React.FC<{ voyant: Voyant; compact?: boolean }> = ({ voyant, compact }) => (
  <span data-testid="voyant" data-voyant={voyant} className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold ${COULEUR_VOYANT[voyant].texte}`}>
    <span aria-hidden className={`h-2 w-2 rounded-full ${COULEUR_VOYANT[voyant].point}`} />
    <span className={compact ? 'max-lg:sr-only' : ''}>{LIBELLES_VOYANT[voyant]}</span>
  </span>
);

/** Un interrupteur lisible, plutôt qu'une case à cocher perdue dans la page. */
const Interrupteur: React.FC<{
  actif: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}> = ({ actif, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={actif}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!actif)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sapin-600
      before:absolute before:-inset-2 before:content-[''] lg:before:content-none
      disabled:opacity-60 ${actif ? 'bg-sapin-700' : 'bg-trait'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform
        ${actif ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

/** Confirmation brève, à côté de la commande qui vient d'être enregistrée. */
const Enregistre: React.FC = () => (
  <span role="status" className="inline-flex items-center gap-1 text-sm font-medium text-statut-resolu">
    <Check className="w-4 h-4" aria-hidden /> Enregistré
  </span>
);

/** Entrée du menu qui n'est pas une rubrique de réglages. */
const VUE_CONSOMMATION = 'consommation-ia';

const ParametresView: React.FC<{
  /** Ouvre un dossier depuis « Consommation de l'IA » (absent : lignes non cliquables). */
  onOuvrirDossier?: (ticketId: string) => void;
}> = ({ onOuvrirDossier }) => {
  const [parametres, setParametres] = useState<ParametreDetaille[]>([]);
  const [categories, setCategories] = useState<CategorieParametres[]>([]);
  const [dependances, setDependances] = useState<EtatDependances | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<Record<string, true>>({});
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [confirmes, setConfirmes] = useState<Record<string, true>>({});
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [visibles, setVisibles] = useState<Record<string, true>>({});
  const [brouillons, setBrouillons] = useState<Record<string, string>>({});
  // « Autre modèle… » choisi dans la liste : la saisie libre s'affiche.
  const [modeleLibre, setModeleLibre] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [rubrique, setRubrique] = useState<string | null>(null);
  const [aRetablir, setARetablir] = useState<ParametreDetaille | null>(null);
  const [testIA, setTestIA] = useState<EtatTestIA | null>(null);

  // Lecture facultative : sans elle, aucun avertissement de dépendance.
  const chargerDependances = useCallback(() => {
    AdminApi.getSettingsDependances()
      .then(r => setDependances(r.dependances))
      .catch(() => { /* état inconnu : on n'affirme rien */ });
  }, []);

  useEffect(() => {
    let vivant = true;
    AdminApi.getSettings()
      .then(r => {
        if (!vivant) return;
        setParametres(r.parametres);
        setCategories(r.categoriesDetail ?? r.categories.map(nom => ({ nom, description: null })));
      })
      .catch(e => { if (vivant) setErreurChargement(messageErreur(e)); })
      .finally(() => { if (vivant) setChargement(false); });
    chargerDependances();
    return () => { vivant = false; };
  }, [chargerDependances]);

  const rubriques = useMemo(() => grouperParCategorie(parametres, categories), [parametres, categories]);
  const resultats = useMemo(
    () => (recherche.trim() ? grouperParCategorie(filtrerParametres(parametres, recherche), categories) : null),
    [parametres, recherche, categories],
  );
  const consommationOuverte = !resultats && rubrique === VUE_CONSOMMATION;
  const rubriqueActive = rubriques.find(g => g.nom === rubrique) ?? rubriques[0] ?? null;
  const affichees = resultats ?? (rubriqueActive && !consommationOuverte ? [rubriqueActive] : []);
  const voyants = useMemo(
    () => new Map(rubriques.map(g => [g.nom, voyantRubrique(g.parametres, parametres, dependances)])),
    [rubriques, parametres, dependances],
  );

  /** Enregistre une valeur. `optimiste` : l'écran l'affiche tout de suite et
   *  revient à l'ancienne valeur si le serveur refuse (interrupteur, liste). */
  async function enregistrer(p: ParametreDetaille, valeur: string | null, optimiste = false) {
    setEnCours(e => ({ ...e, [p.cle]: true }));
    setErreurs(e => sans(e, p.cle));
    setConfirmes(c => sans(c, p.cle));
    if (optimiste) {
      setParametres(liste => liste.map(x => (x.cle === p.cle ? { ...x, valeur, parDefaut: false } : x)));
    }
    try {
      const r = await AdminApi.updateSetting(p.cle, valeur);
      setParametres(r.parametres);
      setBrouillons(b => sans(b, p.cle));
      if (p.type === 'SECRET') {
        setSecrets(s => sans(s, p.cle));
        setVisibles(v => sans(v, p.cle));
      }
      setConfirmes(c => ({ ...c, [p.cle]: true }));
      window.setTimeout(() => setConfirmes(c => sans(c, p.cle)), 2500);
      // Une clé saisie peut lever un avertissement (clé IA, téléphonie) ; la
      // clé IA attendue change avec le fournisseur choisi.
      if (doitRelireDependances(p)) chargerDependances();
      // Le dernier test de la clé ne vaut plus pour la nouvelle configuration.
      if (p.cle.startsWith('ia.') && p.cle !== 'ia.actif') setTestIA(null);
    } catch (e) {
      if (optimiste) {
        setParametres(liste => liste.map(x => (x.cle === p.cle ? { ...x, valeur: p.valeur, parDefaut: p.parDefaut } : x)));
      }
      setErreurs(er => ({ ...er, [p.cle]: messageErreur(e) }));
    } finally {
      setEnCours(e => sans(e, p.cle));
    }
  }

  async function confirmerRetablir() {
    if (!aRetablir) return;
    const p = aRetablir;
    setARetablir(null);
    await enregistrer(p, null);
  }

  async function testerCleIA() {
    setTestIA({ etat: 'en-cours' });
    try {
      const r = await AdminApi.testerCleIA();
      setTestIA({ etat: 'ok', texte: texteSuccesTestIA(r) });
    } catch (e) {
      setTestIA({ etat: 'echec', ...messageTestIA(e) });
    }
  }

  function annulerBrouillon(cle: string) {
    setBrouillons(b => sans(b, cle));
  }

  function choisirRubrique(nom: string) {
    setRecherche('');
    setRubrique(nom);
  }

  /** Depuis la consommation : revient à la rubrique IA, sur le réglage des tarifs. */
  function allerAuxTarifs() {
    const tarifs = parametres.find(p => p.cle === 'ia.tarifs');
    choisirRubrique(tarifs?.categorie ?? 'Intelligence artificielle');
    window.setTimeout(() => {
      const ligneTarifs = document.getElementById('parametre-ia.tarifs');
      ligneTarifs?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      ligneTarifs?.querySelector('input')?.focus();
    }, 50);
  }

  if (chargement) {
    return (
      <div className="flex items-center justify-center py-20 text-graphite">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des paramètres…
      </div>
    );
  }

  // --- Une ligne de réglage ------------------------------------------------
  function ligne(p: ParametreDetaille) {
    const occupe = Boolean(enCours[p.cle]);
    const brouillon = brouillons[p.cle];
    const modifie = brouillon !== undefined && brouillon !== (p.valeur ?? '');
    const erreurNombre = p.type === 'NOMBRE' && modifie ? validerNombre(p, brouillon) : null;
    const bornes = p.type === 'NOMBRE' ? texteBornes(p) : null;
    const sansEffet = texteSansEffet(p, parametres, dependances);
    const erreur = erreurs[p.cle];
    const confirme = Boolean(confirmes[p.cle]);
    const actif = p.valeur === 'true';

    const enregistrerBrouillon = () => {
      if (occupe || !modifie || erreurNombre) return;
      enregistrer(p, p.type === 'NOMBRE' ? nombreAEnvoyer(brouillon ?? '') : (brouillon ?? '').trim());
    };
    const saisieSecrete = secrets[p.cle] ?? '';
    const enregistrerSecret = () => {
      if (occupe || !saisieSecrete) return;
      enregistrer(p, saisieSecrete);
    };

    return (
      <div key={p.cle} id={`parametre-${p.cle}`} className="px-4 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-encre">{p.libelle}</h3>
            <p className="mt-0.5 text-sm leading-relaxed text-graphite">{p.description}</p>
          </div>
          {p.type === 'BOOLEEN' && (
            <div className="flex shrink-0 items-center gap-3 pt-0.5">
              {confirme && <Enregistre />}
              <span aria-hidden className="hidden w-20 text-right text-sm text-graphite sm:inline">
                {actif ? 'Activé' : 'Désactivé'}
              </span>
              <Interrupteur
                label={p.libelle}
                actif={actif}
                disabled={occupe}
                onChange={v => enregistrer(p, v ? 'true' : 'false', true)}
              />
            </div>
          )}
        </div>

        {p.type !== 'BOOLEEN' && (
          <div data-testid="commandes-parametre" className="mt-3 flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
            {p.type === 'TEXTE' && p.options && (
              <label data-testid="enveloppe-champ" className="block flex-1 min-w-0 max-sm:basis-full sm:flex-none sm:w-64">
                <span className="sr-only">{p.libelle}</span>
                <Select
                  value={p.valeur ?? ''}
                  options={p.options.map(o => ({ value: o, label: libelleOption(p.cle, o) }))}
                  disabled={occupe}
                  onChange={async e => {
                    const modele = parametres.find(x => x.cle === 'ia.modele');
                    await enregistrer(p, e.target.value, true);
                    // Changer de fournisseur : le modèle repasse sur le choix
                    // recommandé du nouveau fournisseur (après l'enregistrement
                    // du fournisseur, pour ne pas croiser les deux réponses).
                    if (p.cle === 'ia.fournisseur' && modele && (modele.valeur ?? '') !== '') {
                      setModeleLibre(false);
                      await enregistrer(modele, '', true);
                    }
                  }}
                />
              </label>
            )}

            {p.cle === 'ia.modele' && (() => {
              const fournisseur = parametres.find(x => x.cle === 'ia.fournisseur')?.valeur || 'gemini';
              const choix = modeleLibre ? MODELE_AUTRE : choixModele(p.valeur, fournisseur);
              return (
                <label data-testid="enveloppe-champ" className="block flex-1 min-w-0 max-sm:basis-full sm:flex-none sm:w-[26rem]">
                  <span className="sr-only">{p.libelle}</span>
                  <Select
                    value={choix}
                    disabled={occupe}
                    options={[
                      ...(MODELES_IA[fournisseur] ?? []).map(m => ({ value: m.valeur, label: m.libelle })),
                      { value: MODELE_AUTRE, label: 'Autre modèle…' },
                    ]}
                    onChange={e => {
                      if (e.target.value === MODELE_AUTRE) { setModeleLibre(true); return; }
                      setModeleLibre(false);
                      enregistrer(p, e.target.value, true);
                    }}
                  />
                </label>
              );
            })()}

            {((p.type === 'TEXTE' && !p.options && (p.cle !== 'ia.modele' || modeleLibre || choixModele(p.valeur, parametres.find(x => x.cle === 'ia.fournisseur')?.valeur || 'gemini') === MODELE_AUTRE)) || p.type === 'NOMBRE') && (
              <>
                <div data-testid="enveloppe-champ" className={`flex-1 min-w-0 max-sm:basis-full sm:flex-none ${p.type === 'NOMBRE' ? 'sm:w-36' : p.cle === 'ia.tarifs' ? 'sm:w-[28rem]' : 'sm:w-80'}`}>
                  <Input
                    type={p.type === 'NOMBRE' ? 'number' : 'text'}
                    name={`parametre-${p.cle}`}
                    autoComplete="off"
                    aria-label={p.libelle}
                    value={brouillon ?? p.valeur ?? ''}
                    placeholder={p.type === 'TEXTE' ? texteAttente(p, parametres) : undefined}
                    onChange={e => setBrouillons(b => ({ ...b, [p.cle]: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') enregistrerBrouillon();
                      if (e.key === 'Escape') annulerBrouillon(p.cle);
                    }}
                  />
                </div>
                <Button
                  variant={modifie && !erreurNombre ? 'primary' : 'secondary'}
                  className={TACTILE}
                  disabled={occupe || !modifie || !!erreurNombre}
                  onClick={enregistrerBrouillon}
                >
                  {occupe && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />}
                  Enregistrer
                </Button>
                {modifie && !occupe && (
                  <Button variant="ghost" className={TACTILE} onClick={() => annulerBrouillon(p.cle)}>
                    Annuler
                  </Button>
                )}
              </>
            )}

            {p.type === 'SECRET' && (
              <>
                <div data-testid="enveloppe-champ" className="flex-1 min-w-0 max-sm:basis-full sm:flex-none sm:w-80">
                  <Input
                    type={visibles[p.cle] ? 'text' : 'password'}
                    name={`secret-${p.cle}`}
                    autoComplete="new-password"
                    aria-label={p.libelle}
                    placeholder={texteAttenteSecret(p, parametres)}
                    value={saisieSecrete}
                    onChange={e => setSecrets(s => ({ ...s, [p.cle]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') enregistrerSecret(); }}
                  />
                </div>
                <Button
                  variant="secondary"
                  className={`${TACTILE} px-2.5`}
                  aria-label={visibles[p.cle] ? 'Masquer la saisie' : 'Afficher la saisie'}
                  title={visibles[p.cle] ? 'Masquer la saisie' : 'Afficher la saisie'}
                  onClick={() => setVisibles(v => (v[p.cle] ? sans(v, p.cle) : { ...v, [p.cle]: true }))}
                >
                  {visibles[p.cle] ? <EyeOff className="w-4 h-4" aria-hidden /> : <Eye className="w-4 h-4" aria-hidden />}
                </Button>
                <Button
                  variant={saisieSecrete ? 'primary' : 'secondary'}
                  className={TACTILE}
                  disabled={occupe || !saisieSecrete}
                  onClick={enregistrerSecret}
                >
                  {occupe && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />}
                  Enregistrer
                </Button>
              </>
            )}

            {!p.parDefaut && !modifie && (
              <Button
                variant="ghost"
                title={p.type === 'SECRET' ? 'Effacer la valeur enregistrée' : 'Rétablir la valeur par défaut'}
                className={TACTILE}
                disabled={occupe}
                onClick={() => setARetablir(p)}
              >
                <RotateCcw className="w-4 h-4 mr-1.5" aria-hidden />
                {p.type === 'SECRET' ? 'Effacer' : 'Valeur par défaut'}
              </Button>
            )}

            {confirme && <Enregistre />}
          </div>
        )}

        {bornes && <p className="mt-1.5 text-xs text-graphite">Valeurs acceptées : {bornes.toLowerCase()}.</p>}
        {erreurNombre && <p role="alert" className="mt-1.5 text-xs font-medium text-statut-urgent">{erreurNombre}</p>}
        {sansEffet && (
          <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-statut-attente">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{sansEffet}</span>
          </p>
        )}
        {erreur && (
          <p role="alert" className="mt-2 flex items-start gap-1.5 text-sm text-statut-urgent">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>Non enregistré : <span>{erreur}</span></span>
          </p>
        )}

        {p.cle === 'ia.cle_api' && blocTestIA(p, saisieSecrete !== '')}
      </div>
    );
  }

  // --- Test de la clé d'IA ---------------------------------------------------
  function blocTestIA(p: ParametreDetaille, saisieEnAttente: boolean) {
    const enTest = testIA?.etat === 'en-cours';
    // La clé peut aussi venir d'une variable du serveur : on ne bloque que si
    // l'on sait qu'aucune clé n'est utilisable.
    const aucuneCle = !p.renseigne && dependances?.cleIa === false;
    const aide = saisieEnAttente
      ? 'Enregistrez la clé saisie avant de la tester.'
      : aucuneCle
        ? 'Enregistrez d’abord une clé.'
        : 'Court appel au fournisseur avec la clé et le modèle enregistrés.';
    return (
      <div className="mt-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button variant="secondary" className={TACTILE} disabled={enTest || saisieEnAttente || aucuneCle} onClick={testerCleIA}>
            {enTest
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />
              : <Zap className="w-4 h-4 mr-1.5" aria-hidden />}
            Tester la clé
          </Button>
          <span className="text-xs text-graphite">{enTest ? 'Test en cours…' : aide}</span>
        </div>
        {testIA?.etat === 'ok' && (
          <div role="status" className="mt-2 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{testIA.texte}</span>
          </div>
        )}
        {testIA?.etat === 'echec' && (
          <div role="alert" className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p>{testIA.titre}</p>
              {testIA.detail && <p className="mt-0.5 text-xs text-red-700 break-words">{testIA.detail}</p>}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Une rubrique ----------------------------------------------------------
  function carteRubrique(groupe: GroupeParametres<ParametreDetaille>) {
    const complete = rubriques.find(g => g.nom === groupe.nom) ?? groupe;
    const voyant = voyants.get(groupe.nom) ?? 'ok';
    const maitre = complete.parametres.find(p => p.cle in INTERRUPTEURS_GENERAUX);
    const manquantes = dependancesRubrique(complete.parametres, dependances);
    const idTitre = `rubrique-${normaliser(groupe.nom).replace(/[^a-z0-9]+/g, '-')}`;

    return (
      <Card key={groupe.nom} className="overflow-hidden">
        <section aria-labelledby={idTitre}>
          <div className="border-b border-trait px-4 py-4 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <h2 id={idTitre} className="flex items-center gap-2 text-lg font-semibold text-encre">
                <span className="text-sapin-700">{iconeRubrique(groupe.nom, 'w-5 h-5')}</span>
                {groupe.nom}
              </h2>
              <Pastille voyant={voyant} />
            </div>
            {groupe.description && <p className="mt-1 text-sm text-graphite">{groupe.description}</p>}

            {maitre && maitre.valeur !== 'true' && (
              <p className="mt-3 flex items-start gap-2 rounded-lg bg-brume px-3 py-2.5 text-sm text-graphite">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  « {maitre.libelle} » est désactivé : les autres réglages de la rubrique sont conservés et
                  s’appliqueront dès qu’il sera activé.
                </span>
              </p>
            )}

            {manquantes.length > 0 && (
              <div
                className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
                  voyant === 'a-completer' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-trait bg-brume text-graphite'
                }`}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <div className="min-w-0">
                  <p className="font-semibold">
                    {voyant === 'a-completer' ? 'À compléter' : 'Avant d’activer certains réglages'}
                  </p>
                  <ul className="mt-0.5 space-y-0.5">
                    {manquantes.map(d => <li key={d}>{DEPENDANCES[d].explication}</li>)}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="divide-y divide-trait">
            {groupe.parametres.map(ligne)}
          </div>

          {normaliser(groupe.nom).includes('intelligence') && !resultats && (
            <div className="border-t border-trait px-4 py-3 sm:px-6">
              <button
                type="button"
                onClick={() => choisirRubrique(VUE_CONSOMMATION)}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold text-sapin-700 hover:text-sapin-900 ${TACTILE}`}
              >
                <BarChart3 className="h-4 w-4" aria-hidden />
                Voir la consommation de l’IA (jetons, coût estimé)
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          )}
        </section>
      </Card>
    );
  }

  const nombreResultats = resultats?.reduce((n, g) => n + g.parametres.length, 0) ?? 0;

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-encre">Paramètres</h1>
          <p className="mt-1 text-sm text-graphite">
            Réglages de la plateforme. Chaque changement s’applique immédiatement, sans redéploiement.
          </p>
        </div>
        <label className="block w-full sm:w-72">
          <span className="sr-only">Rechercher un paramètre</span>
          <Input
            type="search"
            name="recherche-parametres"
            autoComplete="off"
            placeholder="Rechercher un paramètre…"
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
            icon={<Search className="w-4 h-4" />}
          />
        </label>
      </header>

      {erreurChargement && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          <span>Les paramètres n’ont pas pu être chargés : <span>{erreurChargement}</span></span>
        </div>
      )}

      {rubriques.length > 0 && (
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          <nav aria-label="Rubriques des paramètres" className="lg:sticky lg:top-4 lg:w-64 lg:shrink-0">
            <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:rounded-card lg:border lg:border-trait lg:bg-white lg:p-2">
              {rubriques.map(g => {
                const actif = !resultats && !consommationOuverte && g.nom === rubriqueActive?.nom;
                return (
                  <li key={g.nom} className="shrink-0 lg:shrink">
                    <button
                      type="button"
                      aria-current={actif ? 'true' : undefined}
                      onClick={() => choisirRubrique(g.nom)}
                      className={`flex w-full items-center gap-2.5 whitespace-nowrap rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors lg:border-transparent lg:py-2.5 ${TACTILE} ${
                        actif
                          ? 'border-sapin-200 bg-sapin-50 text-sapin-800'
                          : 'border-trait bg-white text-graphite hover:bg-brume hover:text-encre'
                      }`}
                    >
                      <span className={`lg:self-start lg:pt-0.5 ${actif ? 'text-sapin-700' : 'text-graphite'}`}>{iconeRubrique(g.nom)}</span>
                      {/* Ordinateur : le voyant sous le nom, en toutes lettres.
                          Téléphone : un point après le nom, le mot pour les
                          lecteurs d'écran seulement. */}
                      <span className="lg:min-w-0 lg:flex-1">
                        <span className="block lg:truncate">{g.nom}</span>
                        <span className="mt-0.5 hidden lg:block"><Pastille voyant={voyants.get(g.nom) ?? 'ok'} /></span>
                      </span>
                      <span className="lg:hidden"><Pastille voyant={voyants.get(g.nom) ?? 'ok'} compact /></span>
                    </button>
                  </li>
                );
              })}
              {rubriques.some(g => normaliser(g.nom).includes('intelligence')) && (
                <li className="shrink-0 lg:shrink lg:mt-1 lg:border-t lg:border-trait lg:pt-1">
                  <button
                    type="button"
                    aria-current={consommationOuverte ? 'true' : undefined}
                    onClick={() => choisirRubrique(VUE_CONSOMMATION)}
                    className={`flex w-full items-center gap-2.5 whitespace-nowrap rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors lg:border-transparent lg:py-2.5 ${TACTILE} ${
                      consommationOuverte
                        ? 'border-sapin-200 bg-sapin-50 text-sapin-800'
                        : 'border-trait bg-white text-graphite hover:bg-brume hover:text-encre'
                    }`}
                  >
                    <span className={consommationOuverte ? 'text-sapin-700' : 'text-graphite'}><BarChart3 className="w-4 h-4" aria-hidden /></span>
                    <span className="lg:min-w-0 lg:flex-1 lg:truncate">Consommation de l’IA</span>
                  </button>
                </li>
              )}
            </ul>
          </nav>

          <div className="min-w-0 flex-1 space-y-5">
            {resultats && nombreResultats > 0 && (
              <p className="text-sm text-graphite">
                {nombreResultats} réglage{nombreResultats > 1 ? 's' : ''} trouvé{nombreResultats > 1 ? 's' : ''} pour « {recherche.trim()} ».
              </p>
            )}

            {resultats && nombreResultats === 0 && (
              <Card className="py-10 text-center">
                <p className="font-medium text-encre">Aucun paramètre ne correspond à « {recherche} ».</p>
                <button
                  type="button"
                  onClick={() => setRecherche('')}
                  className={`mt-2 text-sm font-semibold text-sapin-700 hover:text-sapin-900 ${TACTILE}`}
                >
                  Effacer la recherche
                </button>
              </Card>
            )}

            {affichees.map(carteRubrique)}

            {consommationOuverte && (
              <ConsommationIA onOuvrirDossier={onOuvrirDossier} onReglerTarifs={allerAuxTarifs} />
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={aRetablir !== null}
        onClose={() => setARetablir(null)}
        title={aRetablir?.type === 'SECRET' ? 'Effacer la valeur enregistrée ?' : 'Revenir à la valeur par défaut ?'}
        size="sm"
      >
        {aRetablir && (
          <div className="space-y-4">
            <p className="text-sm text-graphite">
              {aRetablir.type === 'SECRET'
                ? `La valeur enregistrée pour « ${aRetablir.libelle} » sera effacée. Le serveur utilisera sa propre configuration s’il en a une.`
                : `« ${aRetablir.libelle} » reprendra sa valeur par défaut : ${libelleValeurParDefaut(aRetablir)}. Le changement s’applique immédiatement.`}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={() => setARetablir(null)} className={`w-full sm:w-auto ${TACTILE}`}>Annuler</Button>
              <Button onClick={confirmerRetablir} className={`w-full sm:w-auto ${TACTILE}`}>
                {aRetablir.type === 'SECRET' ? 'Effacer' : 'Rétablir'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ParametresView;
